import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCompany, withYear } from "@/config";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const company = useCompany();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState(null);  // { id, question }
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const captchaPayload = captcha?.id ? { id: captcha.id, answer: captchaAnswer } : null;
    const res = await login(email, password, captchaPayload);
    setBusy(false);
    if (res.ok) {
      setCaptcha(null); setCaptchaAnswer("");
      toast.success("Welcome back");
      return;
    }
    if (res.captcha) {
      // Server either required a captcha for the first time, or rejected our
      // answer and issued a fresh one. Swap in the new challenge and keep the
      // email/password filled so the user only re-types the puzzle.
      setCaptcha(res.captcha);
      setCaptchaAnswer("");
      toast.message(res.error);
      return;
    }
    // Any non-captcha error (401, 429, 403) — invalidate the stale challenge.
    setCaptcha(null); setCaptchaAnswer("");
    toast.error(res.error);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 text-white p-12 flex-col justify-between relative overflow-hidden"
           style={{ backgroundColor: company.brand.secondaryColor }}>
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: `linear-gradient(45deg, transparent 48%, ${company.brand.primaryColor} 49%, ${company.brand.primaryColor} 51%, transparent 52%)`, backgroundSize: "80px 80px" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src={company.brand.logo} alt={company.shortName} className="h-11 w-11 object-contain bg-white p-0.5" />
            <div className="font-head font-bold text-2xl tracking-tighter">{company.shortName}</div>
          </div>
        </div>
        <div className="relative">
          <div className="eyebrow mb-4 text-sky-200">Operations Suite</div>
          <h1 className="font-head text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95] mb-6">
            Time. <span style={{ color: company.brand.primaryColor }}>Tracked.</span><br />Projects. Delivered.
          </h1>
          <p className="text-sky-100/80 max-w-md leading-relaxed">
            A rigorous timesheet system for engineering teams. Clock-in, split hours across projects, approve timesheets, and run reports — all in one place.
          </p>
        </div>
        <div className="relative eyebrow text-sky-200/60">
          {withYear(company.extras?.footerCopyright || `© {YEAR} ${company.shortName} — Internal Use`)}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F7F7F5]">
        <form onSubmit={onSubmit} className="w-full max-w-md bg-white border border-[#E5E5E5] p-8 lg:p-10">
          <div className="eyebrow mb-3">Sign In</div>
          <h2 className="font-head text-3xl font-bold tracking-tighter mb-8">Access your workspace</h2>
          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <Input id="email" data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 rounded-none border-[#E5E5E5] h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow">Password</Label>
              <Input id="password" data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2 rounded-none border-[#E5E5E5] h-11" />
            </div>
            {captcha && (
              <div className="border-l-2 border-[#FF5500] bg-[#FFF7F2] p-3" data-testid="captcha-wrap">
                <Label htmlFor="captcha" className="eyebrow">Quick check · {captcha.question}</Label>
                <Input id="captcha" data-testid="captcha-input" type="text" inputMode="numeric" autoComplete="off"
                  value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} required
                  className="mt-2 rounded-none border-[#E5E5E5] h-11" placeholder="Your answer" autoFocus />
                <p className="text-xs text-neutral-500 mt-2">We ask once we see repeated failed attempts from your network.</p>
              </div>
            )}
            <Button type="submit" data-testid="login-submit" disabled={busy}
              className="w-full h-12 rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white font-semibold tracking-wide">
              {busy ? "Signing in..." : "Sign In →"}
            </Button>
            <div className="text-right">
              <a href="/forgot-password" data-testid="forgot-password-link" className="text-xs text-neutral-500 hover:text-[#1F4E8C] underline underline-offset-2">
                Forgot password?
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
